import { ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { isAustraliaRegion } from '@/helpers/region/regionHelper.js';
import {
    isBankRequirementItem,
    resolveBankAccountRouteName,
} from '@/helpers/profile/bankAccountRoute.js';
import {
    groupMissingRequirements,
    isAbnGroupItem,
    isBillingGroupItem,
    isSuperGroupItem,
    resolveAbnGroupRoute,
    resolveBillingGroupRoute,
} from '@/helpers/profile/groupProfileRequirements.js';
import { evaluateSuperRequirementsClient } from '@/helpers/profile/superRequirementRules.js';

export const CONTEXT_JOB_BOOKING = 'job_booking';

/**
 * Profile requirements only apply to shifts starting on/after this date.
 * Mirrors ProfileRequirementsService::REQUIREMENTS_EFFECTIVE_FROM on the backend.
 */
export const REQUIREMENTS_EFFECTIVE_FROM = '2026-07-01';

function normalizeShiftDate(date) {
    if (date == null || date === '') {
        return null;
    }
    return String(date).slice(0, 10).replace(/\//g, '-');
}

/**
 * @param {string|Date|null} date - A shift start date.
 * @returns {boolean} True when the shift falls within the requirement window.
 */
export function shiftRequiresProfileRequirements(date) {
    const normalized = normalizeShiftDate(date);
    return normalized != null && normalized >= REQUIREMENTS_EFFECTIVE_FROM;
}

const COMPANY_BILLING_TYPE = 1;
const SOLE_TRADER_TYPE = 2;

const modalVisible = ref(false);
const modalMissing = ref([]);

const BILLING_CLIENT_RULES = [
    {
        key: 'abn',
        label: 'Australian Business Number (ABN)',
        route: 'profile_edit',
        scrollTo: 'payout-details-abn',
    },
    { key: 'account_name', label: 'Bank account name', navigation: 'bank_account' },
    { key: 'bsb', label: 'BSB', navigation: 'bank_account' },
    { key: 'account_number', label: 'Account number', navigation: 'bank_account' },
];

function isFilled(value) {
    return value != null && String(value).trim() !== '';
}

function resolveBillingAccountType(userData, requirements = null) {
    if (requirements?.billingAccountType != null) {
        return Number(requirements.billingAccountType);
    }
    if (requirements?.requiresSuper === false) {
        return COMPANY_BILLING_TYPE;
    }
    const fromBilling = userData?.billing?.billingAccountType ?? userData?.billing?.type;
    const fromRoot = userData?.billingAccountType;
    return Number(fromBilling ?? fromRoot ?? 0);
}

function shouldRequireSuper(userData, requirements = null) {
    if (requirements?.requiresSuper === true) {
        return true;
    }
    if (requirements?.requiresSuper === false) {
        return false;
    }
    return resolveBillingAccountType(userData, requirements) === SOLE_TRADER_TYPE;
}

function stripSuperFromMissing(missing, userData, requirements = null) {
    if (!shouldRequireSuper(userData, requirements)) {
        return missing.filter((item) => !isSuperGroupItem(item));
    }
    return missing;
}

/**
 * Fallback when job-scoped profileRequirements is unavailable (/me only).
 * Normal locum: billing always; super when sole trader only.
 */
function evaluateClientSide(userData, requirements = null) {
    const valueByKey = {
        abn: userData?.abn,
        account_name: userData?.billing?.bankAccountName,
        bsb: userData?.billing?.bankBsb,
        account_number: userData?.billing?.bankAccountNumber,
    };

    const billingMissing = BILLING_CLIENT_RULES.filter((rule) => !isFilled(valueByKey[rule.key])).map((rule) => ({
        key: rule.key,
        label: rule.label,
        route: rule.route ?? null,
        navigation: rule.navigation ?? null,
        scrollTo: rule.scrollTo ?? null,
        complete: false,
    }));

    const superMissing = shouldRequireSuper(userData, requirements)
        ? evaluateSuperRequirementsClient(userData?.locumSuper).map((rule) => ({
            ...rule,
            label: null,
            navigation: null,
            scrollTo: null,
        }))
        : [];

    const missing = stripSuperFromMissing(
        groupMissingRequirements([...billingMissing, ...superMissing]),
        userData,
        requirements
    );

    return {
        canProceed: missing.length === 0,
        missing,
        complete: missing.length === 0,
    };
}

function isTruthyFlag(value) {
    return value === true || value === 1 || value === '1';
}

function resolveFromRequirements(requirements, userData = null) {
    if (
        !requirements ||
        requirements.context !== CONTEXT_JOB_BOOKING ||
        typeof requirements.complete !== 'boolean'
    ) {
        return null;
    }

    const skipped = isTruthyFlag(requirements.skipped);
    const missing = stripSuperFromMissing(
        groupMissingRequirements(requirements.missing || []),
        userData,
        requirements
    );

    const canProceed = skipped || missing.length === 0;

    return {
        canProceed,
        missing,
        complete: canProceed,
        skipped,
    };
}

export function useProfileRequirements() {
    const store = useStore();
    const router = useRouter();
    const isAustralia = isAustraliaRegion();

    function checkJobBooking(options = {}) {
        if (!isAustralia.value) {
            return { canProceed: true, missing: [], complete: true };
        }

        const {
            userData: userDataOverride = null,
            jobProfileRequirements = null,
            pharmacyId = null,
            isContractorForPharmacy = false,
            contractorInvoicingEnabled = false,
            internalStaffForPharmacy = false,
        } = options;

        const data = userDataOverride || store.getters.GET_USER_DATA;

        const jobScoped = resolveFromRequirements(jobProfileRequirements, data);
        if (jobScoped) {
            return jobScoped;
        }

        // Fallback when profileRequirements was stripped from store but pool flags remain.
        if (isTruthyFlag(internalStaffForPharmacy)) {
            return { canProceed: true, missing: [], complete: true, skipped: true };
        }
        if (isTruthyFlag(isContractorForPharmacy) && !isTruthyFlag(contractorInvoicingEnabled)) {
            return { canProceed: true, missing: [], complete: true, skipped: true };
        }

        // Job booking: do not use global /me requirements (no pharmacy context).
        if (!pharmacyId) {
            const userScoped = resolveFromRequirements(data?.profileRequirements, data);
            if (userScoped) {
                return userScoped;
            }
        }

        return evaluateClientSide(data, jobProfileRequirements);
    }

    function openMissingModal(missing) {
        modalMissing.value = missing || [];
        modalVisible.value = true;
    }

    function closeModal() {
        modalVisible.value = false;
    }

    function goToRequirement(routeNameOrItem) {
        closeModal();

        const item = typeof routeNameOrItem === 'object' ? routeNameOrItem : null;
        let routeName = item ? item.route : routeNameOrItem;
        let scrollTo = item ? (item.scrollTo || item.scroll_to) : null;

        const userData = store.getters.GET_USER_DATA;

        if (item && isAbnGroupItem(item)) {
            router.push(resolveAbnGroupRoute());
            return;
        }

        if (item && isBillingGroupItem(item)) {
            router.push(resolveBillingGroupRoute(item, userData));
            return;
        }

        if (item && isSuperGroupItem(item)) {
            router.push({ name: 'profile_super' });
            return;
        }

        if (item && isBankRequirementItem(item)) {
            routeName = resolveBankAccountRouteName(userData);
            scrollTo = null;
        }

        if (!routeName) {
            return;
        }

        router.push({
            name: routeName,
            ...(scrollTo ? { query: { scroll: scrollTo } } : {}),
        });
    }

    function guardJobBooking(onProceed, options = {}) {
        const { canProceed, missing } = checkJobBooking(options);

        if (!canProceed) {
            openMissingModal(missing);
            return false;
        }

        if (typeof onProceed === 'function') {
            onProceed();
        }

        return true;
    }

    return {
        checkJobBooking,
        guardJobBooking,
        openMissingModal,
        closeModal,
        goToRequirement,
        shiftRequiresProfileRequirements,
        modalVisible,
        modalMissing,
    };
}
